import "./App.css";
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PublicLayout from "./WebPages/Layout/PublicLayout";
import Home from "./WebPages/Pages/Home";
import Restaurants from "./WebPages/Pages/Restaurants";
import About from "./WebPages/Pages/About";
import Cart from "./WebPages/Pages/Cart";
import RestaurantManagement from "./Restaurant/RestaurantManagement";
import Partner from "./WebPages/Pages/Partner";
import AdminApp from "./AdminPanel/AdminApp";
import ResetPassword from "./WebPages/Pages/ResetPassword";
import RestaurantMenu from "./WebPages/Components/Restaurant/RestaurantMenu";
import ScrollToTop from "./WebPages/Components/ScrollToTop";
import { UserProfileDetails } from "./WebPages/Components/UserProfile/AccountDetails/UserProfileDetails";
import OrderDetails from './WebPages/Components/UserProfile/UserOrders/OrderDetails'
import { CartProvider } from "./Api/CartContext";
import PrivacyPolicy from "./WebPages/Pages/PrivacyPolicy";
import TermsAndConditions from "./WebPages/Pages/TermsAndConditions";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check initial authentication
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
      setIsLoading(false);
    };

    // Check auth on mount
    checkAuth();

    // Listen for logout events
    const handleLogoutEvent = () => {
      setIsAuthenticated(false);
    };

    // Listen for storage changes (in case token is removed in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setIsAuthenticated(!!e.newValue);
      }
    };

    window.addEventListener('logout', handleLogoutEvent);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('logout', handleLogoutEvent);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>; // Or your loading component
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Admin Protected Route Component
const AdminProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check initial authentication
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsAuthenticated(!!token);
      setIsLoading(false);
    };

    // Check auth on mount
    checkAuth();

    // Listen for logout events
    const handleLogoutEvent = () => {
      setIsAuthenticated(false);
    };

    // Listen for storage changes (in case token is removed in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        setIsAuthenticated(!!e.newValue);
      }
    };

    window.addEventListener('logout', handleLogoutEvent);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('logout', handleLogoutEvent);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>; // Or your loading component
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};
function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <ScrollToTop />

        <Routes>
          {/* Public Site - including cart routes */}
          <Route path="/" element={<PublicLayout />}>
            <Route index element={<Home />} />
            <Route path="/restaurants" element={<Restaurants />} />
            <Route path="/about" element={<About />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />

            {/* Protected User Routes */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <UserProfileDetails />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/userorders" 
              element={
                <ProtectedRoute>
                  <OrderDetails />
                </ProtectedRoute>
              } 
            />

            {/* Cart routes - you might want to protect these too */}
            <Route path="/cart" element={<Cart />} />
            <Route path="/restaurantMenu/:id" element={<RestaurantMenu />} />
          </Route>

          {/* Protected Admin Panel */}
          <Route 
            path="/admin/*" 
            element={
              <AdminProtectedRoute>
                <AdminApp />
              </AdminProtectedRoute>
            } 
          />
          
          {/* Protected Restaurant Management */}
          <Route 
            path="/restaurantmanage" 
            element={
              <ProtectedRoute>
                <RestaurantManagement />
              </ProtectedRoute>
            } 
          />
          
          {/* Reset password route - public */}
          <Route path="/reset-password/:token" element={<ResetPassword />} />
        </Routes>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;